import React, { useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { useToast } from "../../hooks/useToast";
import { IntegrationAccessRightsForm } from "../../forms/IntegrationAccessRightsForm";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { VStack, HStack } from "@chakra-ui/layout";
import { Skeleton, SkeletonText } from "@chakra-ui/react";

export const IntegrationAccessRights = () => {
  const { businessId, integrationId } = useParams();
  const navigate = useNavigate();
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();

  const { data: integration, isLoading: integrationIsLoading } =
    useAdminApiRequest(
      (apiClient) => {
        if (!businessId || !integrationId) {
          return null;
        }
        return apiClient.getBusinessIntegration(businessId, integrationId);
      },
      [businessId, integrationId]
    );

  const {
    data: accessRights,
    isLoading: accessRightsIsLoading,
    refresh,
  } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !integrationId) {
        return null;
      }
      return apiClient.getIntegrationPermissions(businessId, integrationId);
    },
    [businessId, integrationId]
  );

  const isLoading = integrationIsLoading || accessRightsIsLoading;

  const onSuccess = useCallback(() => {
    showSuccessToast("Integration permissions updated.");
    refresh();
    navigate(
      `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`,
      { replace: true }
    );
  }, [showSuccessToast, businessId, integration, refresh]);

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Integration permissions update failed.");
    },
    [showErrorToast]
  );

  return (
    <>
      <PageHeading>
        <BusinessBreadcrumbs>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations`}
            >
              Integrations
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations/${integration?.id}`}
            >
              {integration?.IntegrationName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations/${integration?.id}/permissions`}
            >
              Permissions
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BusinessBreadcrumbs>
        <PageHeading.Title>Integration Permissions</PageHeading.Title>
      </PageHeading>
      <PageContent>
        {(isLoading && (
          <VStack
            w="100%"
            p={6}
            boxShadow="lg"
            alignItems="flex-start"
            spacing={4}
          >
            <Skeleton w="100px" height="40px" />
            <SkeletonText w="100%" mt="4" noOfLines={20} spacing="4" />
            <HStack w="100%">
              <Skeleton w="80px" height="40px" />
              <Skeleton w="80px" height="40px" />
              <Skeleton w="80px" height="40px" />
            </HStack>
          </VStack>
        )) || (
          <IntegrationAccessRightsForm
            businessId={businessId!}
            integrationId={integration?.id!}
            accessRights={accessRights}
            apiClient={apiClient}
            onCancel={() => {
              navigate(
                `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`
              );
            }}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
        )}
      </PageContent>
    </>
  );
};
